import { Injectable } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { TagGroups, TagGroupsApiService } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { lastValueFrom } from 'rxjs';
import { addTagToArray, deduplicateTags, extractIdsFromTagGroup } from '../util';
import { AssetTagsService } from './asset-tags.service';
import { ValueListsService } from './value-lists.service';

@Injectable({
  providedIn: 'root',
})
export class CoreAssetsTagsService {
  constructor(
    private assetTagsService: AssetTagsService,
    private valueListsService: ValueListsService,
    private tagGroupsApiService: TagGroupsApiService,
  ) {}

  /**
   * Handle tag addition based on whether it is a tag group or a simple tag
   * @param event
   * @param targetArray
   */
  async handleTagAddition(event: AnyType, targetArray: string[]) {
    try {
      if (event.tagGroup) {
        const tagGroups = await this.fetchTagGroups(event._id);
        await this.handleTagGroups(tagGroups, targetArray);
      } else {
        addTagToArray(event, targetArray);
      }
    } catch (error) {
      console.error('Error fetching tag groups:', error);
    }
  }

  /**
   * Fetch tag groups by ID
   * @param tagGroupId
   * @returns
   */
  async fetchTagGroups(tagGroupId: string): Promise<TagGroups> {
    return lastValueFrom(this.tagGroupsApiService.tagGroupsControllerFindOne(tagGroupId));
  }

  updateTagsArray(tagsArray: string[], formGroup: AbstractControl, fieldName: string): void {
    tagsArray.sort((a, b) => a.localeCompare(b));

    const controlValues = formGroup.value;
    controlValues[fieldName] = tagsArray;

    this.assetTagsService.updateTagsArray(tagsArray);

    formGroup.patchValue(controlValues);
    formGroup.get(fieldName)?.markAsDirty();
    formGroup.get(fieldName)?.markAsTouched();
  }

  /**
   * Resolve tags by IDs and add them to the target array
   * @param ids
   * @param targetArray
   */
  async resolveAndAddTags(ids: string[], targetArray: string[]) {
    const resolvedTags = (await this.valueListsService.listTags(ids)).map((tag) => tag.name);
    targetArray.push(...resolvedTags);
    deduplicateTags(targetArray);
  }

  /**
   * Handle tag groups and resolve tags
   * @param tagGroups
   * @param targetArray
   */
  async handleTagGroups(tagGroups: TagGroups, targetArray: string[]) {
    const ids = extractIdsFromTagGroup(tagGroups);
    await this.resolveAndAddTags(ids, targetArray);
  }
}
